import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  InputAdornment,
  ListItemText,
  FormHelperText,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { CommonTexts, StatsLabels, centersTexts } from "locale/es";
import React, { useState } from "react";
import { AppColors } from "resources/AppColors";
import { makeStyles } from "@mui/styles";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { useDispatch } from "react-redux";
import { toast } from "redux/reducers/notificationReducer";
import { Controller, useController } from "react-hook-form";

const useStyles = makeStyles((theme) => ({
  textFieldLabel: {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: AppColors.PRIMARY,
      },
    },
    "& .MuiInputBase-root": {
      color: AppColors.PRIMARY,
    },
    "& .MuiInputLabel-root": {
      color: AppColors.PRIMARY,
    },
    color: AppColors.PRIMARY,
    backgroundColor: AppColors.BOX_FILL,
  },
  radioGroupColor: {
    color: AppColors.BLACK,
    opacity: "0.9",
  },
  selectLabel: {
    "&.MuiInputLabel-shrink": {
      paddingRight: theme.spacing(0.5),
      backgroundColor: AppColors.WHITE,
    },
  },
}));

const optionsConfigCenter = [
  { name: CommonTexts.HEALTH_DATA, value: "healthDataService" },
  { name: CommonTexts.NOTES, value: "noteService" },
  { name: CommonTexts.MESSAGES, value: "messageService" },
  { name: CommonTexts.CONTENT, value: "contentService" },
  { name: CommonTexts.FORMS, value: "formService" },
];

const registrationOptions = [
  { label: "Si", value: true },
  { label: "No", value: false },
];

const ConfigCenterOptions = ({
  control,
  healthDataOptions,
  selectedCenter,
}) => {
  const classes = useStyles();

  const dispatch = useDispatch();

  const [centerComponentSelectionWarning, setCenterComponentSelectionWarning] =
    useState(false);

  const {
    field: {
      ref,
      value: centerComponentsValue,
      onChange: changeCenterComponents,
    },
  } = useController({
    name: "centerComponents",
    control,
  });

  function handleCopyURLToClipboard() {
    navigator.clipboard.writeText(
      selectedCenter?.configuration?.registrationLink
    );
    dispatch(toast("Link copied to clipboard!", "success"));
  }

  function handleCenterComponents(onChange, component, configObject) {
    setCenterComponentSelectionWarning(false);

    const enabledComponents = Object.values(configObject).filter(
      (value) => !!value
    );
    const inComingValue = !configObject[component];

    if (enabledComponents.length > 1 || inComingValue === true) {
      onChange({ ...configObject, [component]: inComingValue });
    } else {
      setCenterComponentSelectionWarning(true);
    }
  }

  return (
    <>
      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls='panel1-content'
          id='panel1-header'
        >
          <Typography variant='body_bold'>
            {centersTexts.CENTER_CONFIG}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 2,
              mt: -2,
            }}
          >
            <Box>
              <FormControl component='fieldset'>
                <FormLabel>
                  <Typography variant='body_card'>
                    {centersTexts.CENTER_COMPONENTS}
                  </Typography>
                </FormLabel>
                <FormGroup aria-label='position'>
                  {optionsConfigCenter &&
                    optionsConfigCenter.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        control={
                          <Checkbox
                            checked={centerComponentsValue[option.value]}
                            onChange={() =>
                              handleCenterComponents(
                                changeCenterComponents,
                                option.value,
                                centerComponentsValue
                              )
                            }
                            inputRef={ref}
                          />
                        }
                        label={option.name}
                        labelPlacement='end'
                      />
                    ))}
                </FormGroup>
                {centerComponentSelectionWarning && (
                  <FormHelperText sx={{ color: AppColors.RED }}>
                    {centersTexts.COMPONENTS_MIN}
                  </FormHelperText>
                )}
              </FormControl>
              <Controller
                control={control}
                name='healthDataOptions'
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    {centerComponentsValue.healthDataService && (
                      <FormControl
                        className={classes.formControl}
                        size='small'
                        sx={{
                          justifyContent: "space-between",
                          mt: 5,
                          ml: 4,
                        }}
                      >
                        <InputLabel
                          id='select-label'
                          className={classes.selectLabel}
                        >
                          {centersTexts.DASHBOARD_CONFIG}
                        </InputLabel>
                        <Select
                          labelId='select-label'
                          id='select'
                          variant='outlined'
                          className={classes.textFieldLabel}
                          multiple
                          value={value}
                          label={centersTexts.DASHBOARD_CONFIG}
                          onChange={onChange}
                          onBlur={onBlur}
                          input={
                            <OutlinedInput id='select-multiple' label='Chip' />
                          }
                          MenuProps={{
                            anchorOrigin: {
                              vertical: "top",
                              horizontal: "left",
                            },
                            transformOrigin: {
                              vertical: "top",
                              horizontal: "left",
                            },
                            getContentAnchorEl: null,
                          }}
                          renderValue={(selected) => (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {selected.map((option) => (
                                <Chip
                                  key={option}
                                  label={StatsLabels[option.toUpperCase()]}
                                  sx={{ textTransform: "capitalize" }}
                                />
                              ))}
                            </Box>
                          )}
                        >
                          {healthDataOptions.map((option) => (
                            <MenuItem
                              key={option}
                              value={option}
                              sx={{ textTransform: "capitalize" }}
                            >
                              {StatsLabels[option.toUpperCase()]}
                              <ListItemText primary={option.name} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </>
                )}
              />
            </Box>
          </Box>
          <Box
            sx={{
              mt: 1,
            }}
          >
            <Controller
              control={control}
              name='maxParticipants'
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='number'
                  label={centersTexts.PARTICIPANTS_LABEL}
                  variant='outlined'
                  className={classes.textFieldLabel}
                  sx={{ minWidth: 200 }}
                  inputProps={{
                    min: 1,
                    max: 999999,
                  }}
                  error={Boolean(error)}
                  helperText={error?.message ?? ""}
                />
              )}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
            <Controller
              control={control}
              name='enabledSelfRegistration'
              render={({ field: { value, onChange } }) => (
                <FormControl>
                  <FormLabel
                    id='demo-row-radio-buttons-group-label'
                    className={classes.radioGroupColor}
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    {centersTexts.REGISTRATION}
                    <RadioGroup
                      row
                      aria-labelledby='demo-row-radio-buttons-group-label'
                      name='row-radio-buttons-group'
                      value={value}
                      onChange={() => {
                        onChange(Boolean(!value));
                      }}
                      sx={{ ml: 1 }}
                    >
                      {registrationOptions.map((option) => (
                        <FormControlLabel
                          key={option.label}
                          value={option.value}
                          control={<Radio />}
                          label={option.label}
                        />
                      ))}
                    </RadioGroup>
                  </FormLabel>
                </FormControl>
              )}
            />
            {selectedCenter &&
              selectedCenter?.configuration?.enabledSelfRegistration &&
              selectedCenter?.configuration?.registrationLink && (
                <TextField
                  sx={{ minWidth: "50%", mt: 1 }}
                  label='Link'
                  value={
                    selectedCenter?.configuration?.registrationLink ||
                    centersTexts.LINK_NO_DATA
                  }
                  margin='normal'
                  disabled
                  variant='outlined'
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          onClick={handleCopyURLToClipboard}
                          size='large'
                        >
                          <FileCopyIcon sx={{ color: AppColors.PRIMARY }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
          </Box>
        </AccordionDetails>
      </Accordion >
    </>
  );
};

export default ConfigCenterOptions;
